import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChatBubbleLeftRightIcon, 
  XMarkIcon, 
  TrashIcon,
  SpeakerWaveIcon,
  ClipboardDocumentIcon,
  MicrophoneIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'ai',
      text: "Welcome to Khatabook AI! To better assist you, I'll need to collect some basic details first. This helps us provide personalized support for your business."
    },
    {
      id: 2,
      sender: 'ai',
      text: "What's your full name?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');

  // Auto-scroll to bottom of messages (mock)
  useEffect(() => {
    // In a real app, you'd attach a ref to the message container and scroll down
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    setMessages([...messages, { id: Date.now(), sender: 'user', text: inputValue }]);
    setInputValue('');
    
    // Mock AI response
    setTimeout(() => {
      setMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: "Thank you! And what's your business name?" }]);
    }, 1000);
  };

  return (
    <>
      {/* Floating Button (Visible when chat is closed) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-[#f43f5e] to-[#fb923c] text-white shadow-lg shadow-red-500/30 transition-shadow"
          >
            <ChatBubbleLeftRightIcon className="h-6 w-6" aria-hidden="true" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-[24px] bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-gray-100 flex flex-col font-sans"
            style={{ height: '600px', maxHeight: 'calc(100vh - 6rem)' }}
          >
            {/* Header */}
            <div className="bg-[#dc343b] p-4 text-white relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                    <div className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 border-2 border-white"></div>
                  </div>
                  <div>
                    <h3 className="font-bold text-[15px] leading-tight">Khatabook AI</h3>
                    <p className="text-[10px] font-bold tracking-widest text-white/70 uppercase mt-0.5">STEP 1 OF 5</p>
                  </div>
                </div>
                
                {/* Header Actions */}
                <div className="flex items-center gap-3">
                  <button className="text-white/80 hover:text-white"><ClipboardDocumentIcon className="h-4 w-4" /></button>
                  <button className="text-white/80 hover:text-white"><SpeakerWaveIcon className="h-4 w-4" /></button>
                  <button className="text-white/80 hover:text-white"><TrashIcon className="h-4 w-4" /></button>
                  <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white ml-1"><XMarkIcon className="h-5 w-5" /></button>
                </div>
              </div>
              
              {/* Pagination Dots */}
              <div className="flex justify-center gap-1.5 mt-4 mb-2">
                <div className="h-1.5 w-6 rounded-full bg-white"></div>
                <div className="h-1.5 w-1.5 rounded-full bg-white/40"></div>
                <div className="h-1.5 w-1.5 rounded-full bg-white/40"></div>
                <div className="h-1.5 w-1.5 rounded-full bg-white/40"></div>
                <div className="h-1.5 w-1.5 rounded-full bg-white/40"></div>
              </div>
            </div>

            {/* Chat Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6 scrollbar-thin scrollbar-thumb-gray-200">
              {messages.map((msg, idx) => (
                <div key={msg.id} className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.sender === 'ai' && (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50 text-[#dc343b] mt-1">
                      <SpeakerWaveIcon className="h-4 w-4" />
                    </div>
                  )}
                  <div 
                    className={`relative rounded-2xl px-4 py-3 text-[15px] leading-relaxed max-w-[85%] shadow-sm ${
                      msg.sender === 'user' 
                        ? 'bg-[#dc343b] text-white rounded-br-sm' 
                        : 'bg-gray-50 text-gray-700 border border-gray-100 rounded-tl-sm'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 bg-white">
              <div className="flex gap-2 items-center">
                <button className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-100 text-gray-500 transition-colors">
                  <MicrophoneIcon className="h-5 w-5" />
                </button>
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Enter your full name..."
                    className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50 pl-4 pr-12 text-[15px] text-gray-900 placeholder-gray-400 focus:border-[#dc343b] focus:outline-none focus:ring-1 focus:ring-[#dc343b]"
                  />
                  <button 
                    onClick={handleSend}
                    className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-lg bg-[#dc343b] text-white hover:bg-[#b91c1c] transition-colors shadow-sm"
                  >
                    <PaperAirplaneIcon className="h-4 w-4 -rotate-45" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
